import { useEffect, useRef, useState } from "react";
import { api, ApiError, type InviteTarget, type OpenInvite } from "../api";
import { useNow } from "../now";
import { t, left, when, word, phrases } from "../i18n";
import { useConfirm } from "./Confirm";
import { CopyButton } from "./Copy";
import type { MadeInvite } from "./InviteLink";
import { Popover } from "./Popover";

// A share's open invites, for its owner (byj-60g), and a template's or a
// file's (byj-1wv.76): its own links that still let someone in, wherever
// they were made — the page, `byj-share`, an earlier visit — as the relay
// lists them (a group's link is the group's, and not among a share's). A
// count beside Invite opens them, one line
// each: its role, how far it has got, how long it has left, and Revoke. The
// relay keeps a link's hash and never the link, so only a link made on this
// page can be copied again.

/** What the open links are read, revoked and replaced through. */
export type InviteCalls = Pick<typeof api, "openInvites" | "revokeInvite" | "replaceInvite">;

/**
 * The open invites to `to`, read again whenever `refresh` changes — a link
 * made on the page, someone new joining — on `reload`, and once a link is
 * revoked or replaced here. Each read leaves any read before it untaken,
 * its answer and its failure alike, so a list from before a change never
 * lands after it. `to` is read by its kind and id, so one made anew each
 * render reads nothing again; `calls` are the relay's, or a page's client
 * as the template and file pages pass theirs. It keeps the links made on
 * the page too (`made`, newest first), the only ones that can be copied
 * again: one made from Invite (`add`) read again with the rest, one
 * replaced here kept in its place, one revoked — or gone already — dropped.
 */
export function useOpenInvites(to: InviteTarget, enabled: boolean, refresh: string, calls: InviteCalls = api) {
  const { kind, id: target } = to;
  const [invites, setInvites] = useState<OpenInvite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [asked, setAsked] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    calls.openInvites({ kind, id: target }).then(
      (xs) => {
        if (!live) return;
        setInvites(xs);
        setError(null);
      },
      (err) => live && setError(err instanceof Error ? err.message : String(err)),
    );
    return () => {
      live = false;
    };
  }, [kind, target, enabled, refresh, asked, calls]);
  const reload = () => setAsked((n) => n + 1);
  const [made, setMade] = useState<MadeInvite[]>([]);
  const keep = (inv: MadeInvite) => setMade((xs) => [inv, ...xs]);
  const drop = (id: string) => setMade((xs) => xs.filter((x) => x.id !== id));
  const add = (inv: MadeInvite) => {
    keep(inv);
    reload();
  };
  // a change to one link, the list read again after it; one the relay no
  // longer has (404) goes from the list, said as InviteGone
  const change = async <T,>(id: string, run: () => Promise<T>): Promise<T> => {
    try {
      return await run();
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 404)) throw err;
      setInvites((xs) => xs.filter((x) => x.id !== id));
      throw new InviteGone();
    } finally {
      reload();
    }
  };
  const revoke = async (id: string) => {
    try {
      await change(id, async () => {
        await calls.revokeInvite({ kind, id: target }, id);
        setInvites((xs) => xs.filter((x) => x.id !== id));
      });
    } catch (err) {
      // gone already: from those made here all the same; any other failure leaves it
      if (err instanceof InviteGone) drop(id);
      throw err;
    }
    drop(id);
  };
  // A new link for what is left of this one, worked out and swapped by the
  // relay in one step (its role and expiry, the uses not taken), the new one
  // in the old one's place, never a moment with neither.
  const replace = (inv: OpenInvite): Promise<MadeInvite> =>
    change(inv.id, async () => {
      const made = await calls.replaceInvite({ kind, id: target }, inv.id);
      const fresh: OpenInvite = { id: made.id, role: made.role, max_uses: made.max_uses, used: 0, expires_at: made.expires_at, created_at: new Date().toISOString() };
      setInvites((xs) => xs.map((x) => (x.id === inv.id ? fresh : x)));
      // made here now: kept to copy
      const kept = { ...made, uses: made.max_uses };
      keep(kept);
      return kept;
    });
  return { invites, error, reload, revoke, replace, made, add };
}

export type OpenInvites = ReturnType<typeof useOpenInvites>;

/** A link the relay no longer has: expired, used up, or revoked elsewhere. */
export class InviteGone extends Error {
  constructor() {
    super(t("session:inviteLinkGone"));
  }
}

/**
 * The count of open links beside Invite, nothing until there is one or the
 * list could not be read; it opens them one line each — the role's badge,
 * uses and time left (the exact expiry in its title), Copy for a link made
 * on this page or else Replace, for one that can be copied, and Revoke, each
 * asked first, and none pressed again while one runs. The question is drawn
 * inside the popover, so answering it does not close it, and once shown the
 * count stays, at 0 too, so revoking the last link leaves the popover open
 * rather than taking it away. Opening it reads the links again and clears
 * the last change's error. In a share's side panel it hangs under the
 * People panel's head, as wide as the panel; `own`, on a page's heading line
 * or a dialog's, it hangs off its own button as the line's other popovers do
 * (`.pop`, `.pop-panel`).
 */
export function InviteLinksChip({ open, own = false }: { open: OpenInvites; own?: boolean }) {
  const [confirm, dialog] = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // time left said again as it runs, and a link that runs out uncounted
  const now = useNow();
  const current = open.invites.filter((inv) => Date.parse(inv.expires_at) >= now);
  const shownOnce = useRef(false);
  if (current.length > 0 || open.error) shownOnce.current = true;
  if (!shownOnce.current) return null;
  const urls = new Map(open.made.map((m) => [m.id, m.url]));
  // one change at a time, asked first; its error said in the popover
  const act = async (question: string, action: string, danger: boolean, run: () => Promise<void>) => {
    if (!(await confirm(question, { action, danger }))) return;
    setError(null);
    setBusy(true);
    try {
      await run();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  const revoke = (inv: OpenInvite) =>
    act(t("session:revokeInviteConfirm", { role: word("common:role", inv.role) ?? inv.role }), t("session:revokeInvite"), true, () => open.revoke(inv.id));
  const replace = (inv: OpenInvite) =>
    act(t("session:replaceInviteConfirm", { role: word("common:role", inv.role) ?? inv.role }), t("session:replaceInvite"), false, async () => {
      await open.replace(inv);
    });
  const shown = error ?? open.error;
  return (
    <Popover
      label={t("session:inviteLinks")}
      button={t("session:inviteLinksCount", { count: current.length })}
      buttonClassName="small"
      className={own ? "pop" : "invite-anchor"}
      popClassName={own ? "pop-panel invite-links-pop" : "invite-pop"}
      focus=".invite-links button"
      onOpen={() => {
        // a fresh look: the links read again, and the last change's error gone
        setError(null);
        open.reload();
      }}
    >
      {() => (
        <>
          {shown && <p className="error">{t("common:error", { m: shown })}</p>}
          {current.length === 0 && !shown && <p className="muted small">{t("session:inviteLinksNone")}</p>}
          <ul className="invite-links">
            {current.map((inv) => {
              const role = word("common:role", inv.role) ?? inv.role;
              const url = urls.get(inv.id);
              return (
                <li key={inv.id}>
                  <span className="invite-link-what">
                    <span className={`badge badge-${inv.role}`}>
                      <span className="badge-role">{role}</span>
                    </span>
                    <span className="muted small invite-link-brief" title={t("session:expires", { when: when(inv.expires_at) })}>
                      {phrases.inviteLinkUses(inv.max_uses, inv.used)} · {left(inv.expires_at, now)}
                    </span>
                  </span>
                  {url ? (
                    <CopyButton text={url} label={t("session:copyInviteLink", { role })} icon />
                  ) : (
                    <button type="button" className="small" disabled={busy} onClick={() => replace(inv)}>
                      {t("session:replaceInvite")}
                    </button>
                  )}
                  <button type="button" className="small danger" disabled={busy} onClick={() => revoke(inv)}>
                    {t("session:revokeInvite")}
                  </button>
                </li>
              );
            })}
          </ul>
          {dialog}
        </>
      )}
    </Popover>
  );
}
